import PropTypes from 'prop-types'
import { useRef } from 'react'

import { useFrame } from '../engine'
import { anim, prog, seg } from '../motion'
import { Box, COLORS, Phone, Shape } from '../primitives'
import { PHONE } from './SceneInvitation'

// Schritte: 0 Codeknacker · 1 Gefühlschaos · 2 Dreamteam
const MEASURES = [
  { label: 'Fähigkeiten', at: [0.2, 0.62] },
  { label: 'Persönlichkeit', at: [0.68, 0.9] },
  { label: 'Kultur', at: [0.71, 0.93] },
  { label: 'Interessen', at: [0.74, 0.96] }
]

const INSIGHTS = [
  {
    kind: 'tri',
    color: 'yellow',
    text: 'Verhalten statt Selbstauskunft',
    at: [0.04, 0.12]
  },
  {
    kind: 'sq',
    color: 'green',
    text: 'Validierte Verfahren der Eignungsdiagnostik',
    at: [0.38, 0.46]
  },
  {
    kind: 'dot',
    color: 'pink',
    text: 'Spielerisch statt Prüfungsgefühl',
    at: [0.7, 0.78]
  }
]

export default function SceneGames() {
  return (
    <>
      <Box
        x={40}
        y={50}
        w={420}
        h={300}
        className='sty-card sty-pad'
        style={anim({ at: [0.01, 0.1] })}
      >
        <div className='sty-eyebrow'>Was gemessen wird</div>
        {MEASURES.map((m, i) => (
          <div
            key={m.label}
            className='sty-measure'
            style={{ top: 70 + i * 54 }}
          >
            <span>{m.label}</span>
            <div className='sty-measure__track'>
              <div
                className='sty-measure__fill'
                style={{ ...prog(m.at), transform: 'scaleX(var(--p))' }}
              />
            </div>
          </div>
        ))}
      </Box>

      {INSIGHTS.map((it, i) => (
        <Box
          key={it.text}
          x={40}
          y={380 + i * 70}
          w={420}
          h={56}
          className='sty-card sty-row sty-pad-x'
          style={anim({ at: it.at, x: -30, y: 0 })}
        >
          <Shape kind={it.kind} color={it.color} size={22} />
          <span className='sty-strong'>{it.text}</span>
        </Box>
      ))}

      <Phone x={PHONE.x} y={PHONE.y}>
        <GameScreens />
      </Phone>
    </>
  )
}

// Texte im Smartphone, Englisch für die Use-Case-Stories
export const GAME_TEXT = {
  de: {
    steps: ['Spiel 1 von 3', 'Spiel 2 von 3', 'Spiel 3 von 3'],
    memory: {
      title: 'Codeknacker',
      watch: 'Merk dir den Code',
      type: 'Jetzt eintippen',
      done: 'Perfekt!'
    },
    emotion: {
      title: 'Gefühlschaos',
      question: 'Welches Gefühl siehst du?',
      options: [
        ['Freude', 'Ärger', 'Angst'],
        ['Trauer', 'Überraschung', 'Ekel'],
        ['Freude', 'Ärger', 'Trauer']
      ]
    },
    swipe: {
      title: 'Dreamteam',
      question: 'Was passt eher zu dir?',
      cards: [
        ['Allein tüfteln', 'Im Team lösen'],
        ['Feste Abläufe', 'Abwechslung']
      ],
      done: 'Geschafft!',
      ready: 'Dein Ergebnis ist fertig.'
    }
  },
  en: {
    steps: ['Game 1 of 3', 'Game 2 of 3', 'Game 3 of 3'],
    memory: {
      title: 'Code Cracker',
      watch: 'Remember the code',
      type: 'Now type it in',
      done: 'Perfect!'
    },
    emotion: {
      title: 'Emotional Chaos',
      question: 'Which emotion do you see?',
      options: [
        ['Joy', 'Anger', 'Fear'],
        ['Sadness', 'Surprise', 'Disgust'],
        ['Joy', 'Anger', 'Sadness']
      ]
    },
    swipe: {
      title: 'Dream Team',
      question: 'Which suits you better?',
      cards: [
        ['Puzzle it out alone', 'Solve it as a team'],
        ['Fixed routines', 'Variety']
      ],
      done: 'Done!',
      ready: 'Your result is ready.'
    }
  }
}

/** Inhalt des Smartphone-Screens mit allen drei Spielen, auch für Version 2. */
export function GameScreens({ text = GAME_TEXT.de }) {
  return (
    <div className='sty-abs sty-screen'>
      <div className='sty-game-head'>
        {text.steps.map((label, i) => (
          <span
            key={label}
            className='sty-abs sty-muted sty-small'
            style={anim({
              at: i === 0 ? [-1, 0] : [i * 0.333, i * 0.333 + 0.03],
              out:
                i < 2 ? [(i + 1) * 0.333 - 0.03, (i + 1) * 0.333] : undefined,
              y: 8,
              oy: -8
            })}
          >
            {label}
          </span>
        ))}
      </div>
      <div className='sty-progress'>
        <div
          className='sty-progress__fill'
          style={{ ...prog([0.02, 0.96]), transform: 'scaleX(var(--p))' }}
        />
      </div>

      <div
        className='sty-abs sty-game'
        style={anim({ at: [-1, 0], out: [0.3, 0.33], y: 0, oy: -20 })}
      >
        <MemoryGame text={text.memory} />
      </div>
      <div
        className='sty-abs sty-game'
        style={anim({
          at: [0.34, 0.38],
          out: [0.63, 0.66],
          y: 20,
          oy: -20
        })}
      >
        <EmotionGame text={text.emotion} />
      </div>
      <div
        className='sty-abs sty-game'
        style={anim({ at: [0.67, 0.71], y: 20 })}
      >
        <SwipeGame text={text.swipe} />
      </div>
    </div>
  )
}

GameScreens.propTypes = { text: PropTypes.object }

// ---------- Codeknacker: Code merken, dann eintippen (Merkfähigkeit) ----------

const SEQ = [4, 0, 8, 2, 6]

function MemoryGame({ text }) {
  const cells = useRef([])
  const hint = useRef(null)

  useFrame((l) => {
    const show = seg(l, 0.03, 0.17)
    const tap = seg(l, 0.18, 0.29)
    const lit =
      show > 0 && show < 1
        ? SEQ[Math.min(SEQ.length - 1, Math.floor(show * SEQ.length))]
        : -1
    const done = SEQ.slice(0, Math.floor(tap * (SEQ.length + 0.99)))
    cells.current.forEach((el, i) => {
      if (!el) return
      el.dataset.state = done.includes(i) ? 'done' : i === lit ? 'lit' : 'idle'
    })
    if (hint.current) {
      hint.current.textContent =
        tap >= 1 ? text.done : show >= 1 ? text.type : text.watch
    }
  })

  return (
    <>
      <div className='sty-strong sty-center'>{text.title}</div>
      <div ref={hint} className='sty-muted sty-small sty-center sty-mt-1'>
        {text.watch}
      </div>
      <div className='sty-memory'>
        {Array.from({ length: 9 }, (_, i) => (
          <div
            key={i}
            ref={(el) => (cells.current[i] = el)}
            className='sty-memory__cell'
            data-state='idle'
          >
            {i + 1}
          </div>
        ))}
      </div>
    </>
  )
}

MemoryGame.propTypes = { text: PropTypes.object.isRequired }

// ---------- Gefühlschaos: Gefühle in Gesichtern erkennen (Emotionswahrnehmung) ----------

const MOUTHS = {
  joy: 'M-18 8 Q0 28 18 8',
  surprise: 'M-7 16 A7 9 0 1 0 7 16 A7 9 0 1 0 -7 16',
  sadness: 'M-16 22 Q0 8 16 22'
}
const BROWS = {
  joy: ['M-24 -20 Q-16 -25 -8 -20', 'M8 -20 Q16 -25 24 -20'],
  surprise: ['M-24 -26 Q-16 -33 -8 -26', 'M8 -26 Q16 -33 24 -26'],
  sadness: ['M-24 -18 L-8 -24', 'M8 -24 L24 -18']
}
const TRIALS = [
  {
    face: 'joy',
    pick: 0,
    at: [0.36, 0.46]
  },
  {
    face: 'surprise',
    pick: 1,
    at: [0.46, 0.55]
  },
  {
    face: 'sadness',
    pick: 2,
    at: [0.55, 0.64]
  }
]

function Face({ mood }) {
  return (
    <svg viewBox='-50 -50 100 100' width='120' height='120' aria-hidden='true'>
      <circle r='46' fill='#FFE3C2' />
      {BROWS[mood].map((d) => (
        <path
          key={d}
          d={d}
          fill='none'
          stroke={COLORS.ink}
          strokeWidth='3.5'
          strokeLinecap='round'
        />
      ))}
      <circle
        cx='-15'
        cy='-8'
        r={mood === 'surprise' ? 5 : 4}
        fill={COLORS.ink}
      />
      <circle
        cx='15'
        cy='-8'
        r={mood === 'surprise' ? 5 : 4}
        fill={COLORS.ink}
      />
      <path
        d={MOUTHS[mood]}
        fill={mood === 'surprise' ? COLORS.ink : 'none'}
        stroke={COLORS.ink}
        strokeWidth='3.5'
        strokeLinecap='round'
      />
    </svg>
  )
}

Face.propTypes = { mood: PropTypes.oneOf(Object.keys(MOUTHS)).isRequired }

function EmotionGame({ text }) {
  return (
    <>
      <div className='sty-strong sty-center'>{text.title}</div>
      <div className='sty-muted sty-small sty-center sty-mt-1'>
        {text.question}
      </div>
      {TRIALS.map((t, k) => (
        <div
          key={t.face}
          className='sty-abs sty-emotion'
          style={anim({
            at: k === 0 ? [-1, 0] : [t.at[0], t.at[0] + 0.015],
            out: k < TRIALS.length - 1 ? [t.at[1] - 0.015, t.at[1]] : undefined,
            x: 40,
            ox: -40,
            y: 0,
            oy: 0
          })}
        >
          <div className='sty-emotion__face'>
            <Face mood={t.face} />
          </div>
          <div className='sty-emotion__options'>
            {text.options[k].map((o, n) => (
              <div key={o} className='sty-swipe__option'>
                {n === t.pick && (
                  <div
                    className='sty-swipe__picked sty-emotion__picked'
                    style={{
                      ...prog([t.at[0] + 0.04, t.at[0] + 0.06]),
                      opacity: 'var(--p)'
                    }}
                  />
                )}
                <span>{o}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  )
}

EmotionGame.propTypes = { text: PropTypes.object.isRequired }

// ---------- Dreamteam: Karten wischen (Kultur) ----------

function SwipeGame({ text }) {
  return (
    <>
      <div className='sty-strong sty-center'>{text.title}</div>
      <div className='sty-muted sty-small sty-center sty-mt-1'>
        {text.question}
      </div>

      <div
        className='sty-swipe'
        style={anim({
          at: [0.79, 0.82],
          out: [0.85, 0.89],
          s: 0.9,
          y: 0,
          ox: -280,
          oy: 30
        })}
      >
        <SwipeCard
          a={text.cards[1][0]}
          b={text.cards[1][1]}
          pick='b'
          pickAt={[0.82, 0.84]}
        />
      </div>
      <div
        className='sty-swipe'
        style={anim({
          at: [0.7, 0.73],
          out: [0.76, 0.8],
          s: 0.9,
          y: 0,
          ox: 280,
          oy: 30
        })}
      >
        <SwipeCard
          a={text.cards[0][0]}
          b={text.cards[0][1]}
          pick='b'
          pickAt={[0.73, 0.75]}
        />
      </div>
      <div
        className='sty-swipe'
        style={anim({ at: [0.9, 0.94], s: 0.6, y: 0 })}
      >
        <div className='sty-done'>
          <div className='sty-row'>
            <Shape kind='tri' color='yellow' size={40} rotate={-10} />
            <Shape kind='sq' color='green' size={34} rotate={12} />
            <Shape kind='dot' color='pink' size={34} />
          </div>
          <div className='sty-h4 sty-mt-3'>{text.done}</div>
          <div className='sty-muted sty-small sty-center'>{text.ready}</div>
        </div>
      </div>
    </>
  )
}

SwipeGame.propTypes = { text: PropTypes.object.isRequired }

function SwipeCard({ a, b, pick, pickAt }) {
  return (
    <div className='sty-swipe__card'>
      {[a, b].map((label, i) => (
        <div key={label} className='sty-swipe__option'>
          {(pick === 'a' ? 0 : 1) === i && (
            <div
              className='sty-swipe__picked'
              style={{ ...prog(pickAt), opacity: 'var(--p)' }}
            />
          )}
          <span>{label}</span>
        </div>
      ))}
    </div>
  )
}

SwipeCard.propTypes = {
  a: PropTypes.string.isRequired,
  b: PropTypes.string.isRequired,
  pick: PropTypes.oneOf(['a', 'b']).isRequired,
  pickAt: PropTypes.arrayOf(PropTypes.number).isRequired
}
